import { kernelQuery } from "../client.js";
import { runContradictionCheck, type ContradictionWarning } from "./smart-capture.js";
// WP-488 Slice 3 (FEAT-1377): type-only — resolved via a tsconfig.json path alias, not an
// npm dependency (see tsconfig.json's comment: Railway's isolated Docker build for this
// package can't resolve a private workspace package). Erased entirely at build time.
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";

export interface SemanticConflictResult {
  chainEntryId: string;
  chainEntryName: string;
  chainEntryDescription: string;
  type: "contradiction" | "supersession" | "duplicate" | "overlap";
  confidence: number;
  explanation: string;
}

export interface ConflictPreflightResult {
  source: "semantic" | "heuristic";
  conflicts: SemanticConflictResult[];
  advisoryWarnings: ContradictionWarning[];
}

/**
 * Run the staging-grade semantic conflict detector when available.
 * Falls back to the existing heuristic warning path if the bridge is unavailable.
 *
 * WP-485 Slice 2b (R2, FEAT-1370): this is DISCOVERY ONLY — it used to also compute
 * `blockingContradictions` via a local `BLOCKING_CONTRADICTION_THRESHOLD` comparison, one of
 * THREE independent copies of the same enforcement decision across the MCP connector (the
 * others: `promote.ts`, `smart-capture.ts`). Deleted: the enforcement decision now happens ONE
 * place, server-side, inside `commitEntry`'s evaluate()/assessContradiction gate (Slice 2a).
 * Callers hand `conflicts` to the server as evidence and render whatever `contradictionAdvisory`
 * the server computes — never decide locally.
 */
export async function runConflictPreflight(
  name: string,
  description: string,
  collectionHint?: string,
): Promise<ConflictPreflightResult> {
  try {
    // WP-488 Slice 3: typed via GatewayRouteReturnByName off the real Convex ref
    // (internal.intelligence.conflictDetection.detectSemanticConflicts's declared
    // `Promise<ConflictResult[]>`) instead of a hand-rolled generic — the server never
    // returns null/undefined (verified: the action's return type is always an array), the
    // `?? []` below stays as defensive runtime handling of a malformed HTTP response, not
    // because the real type allows it. `ConflictResult[]`'s fields are a strict superset of
    // the local `SemanticConflictResult` (extra `chainEntryDocId` etc.) — structurally
    // assignable to the narrower local shape used by `ConflictPreflightResult.conflicts`.
    const conflicts = await kernelQuery<GatewayRouteReturnByName<"chain.detectSemanticConflicts">>("chain.detectSemanticConflicts", {
      name,
      description,
      ...(collectionHint ? { collectionHint } : {}),
    });

    return {
      source: "semantic",
      conflicts: conflicts ?? [],
      advisoryWarnings: [],
    };
  } catch (err) {
    console.error('[conflict-preflight] Semantic conflict detection unavailable, falling back to heuristic:', err instanceof Error ? err.message : String(err));
    const advisoryWarnings = await runContradictionCheck(name, description);
    return {
      source: "heuristic",
      conflicts: [],
      advisoryWarnings,
    };
  }
}
